import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import apiService from '../services/api';
import { EventFilterParams } from '../types';

interface NotificationItem {
  id: number;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

/* Format date like "16 Jan 2026, 03:30 PM" */
const formatDate = (date: string) => {
  const d = new Date(date);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /* Pagination */
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState<EventFilterParams>({
    page: 1,
    limit: 10,
  });

  /* Fetch notifications */
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await apiService.getAllNotifications(filters);

      setNotifications(res?.data || []);
      setTotal(res?.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [filters]);

  /* Pagination handlers */
  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <NotificationsIcon color="primary" />
        <Typography fontWeight={700} sx={{ ml: 1, fontSize: '1.5rem' }}>
          Notifications
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 180,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Notifications List */}
      {!loading && notifications.length > 0 && (
        <Stack
          sx={{
            bgcolor: '#fff',
            borderRadius: 1.5,
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
          }}
        >
          {notifications.map(notification => (
            <Box
              key={notification.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.25,
                gap: 1,
                bgcolor: '#fff',
                transition: 'background 0.2s',
                '&:hover': {
                  bgcolor: '#e0f2fe',
                  cursor: 'pointer',
                  pointerEvents: 'none',
                },
              }}
            >
              {/* Left: unread indicator + text */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!notification.isRead && (
                  <FiberManualRecordIcon
                    sx={{ fontSize: 10, color: 'primary.main' }}
                  />
                )}
                <Box>
                  <Typography fontWeight={600} fontSize="0.95rem">
                    {notification.title}
                  </Typography>
                  <Typography fontSize="0.85rem" color="text.secondary">
                    {notification.message}
                  </Typography>
                </Box>
              </Box>

              {/* Right: timestamp */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                <Typography fontSize="0.75rem" color="text.disabled">
                  {formatDate(notification.createdAt)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      {/* Empty State */}
      {!loading && notifications.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            color: 'text.disabled',
          }}
        >
          <NotificationsIcon sx={{ fontSize: 48 }} />
          <Typography sx={{ mt: 1 }}>No notifications available</Typography>
        </Box>
      )}

      {/* Pagination */}
      {notifications.length > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            mt: 1,
            borderTop: '1px solid #e5e7eb',
            '& .MuiTablePagination-toolbar': { px: 2 },
          }}
        />
      )}
    </Box>
  );
};

export default NotificationsPage;
